import { reactHookForm } from '@latitude/form';
import { useEffect } from 'react';

/**
 * Clears the errors of the questions that became hidden.
 *
 * The yup schema skips hidden questions (see createQuestionnaireResolver), but
 * react-hook-form only re-validates the field that changed. A question that
 * had an error while visible keeps that stale error after a trigger hides it,
 * until the next full validation. This removes those errors as soon as the
 * hidden list changes. Values are kept on purpose: the BE save replaces the
 * whole answer set, so dropping them would delete stored answers.
 *
 * `hiddenQuestionPaths` holds question ids and, for the triggered questions
 * inside group rows, form paths such as `owners.group_answers.1.controlTitle`
 * (see useQuestionnaire).
 */
export const useClearHiddenQuestionErrors = (hiddenQuestionPaths: string[]) => {
  const { useFormContext, get } = reactHookForm;
  const { clearErrors, formState } = useFormContext();
  const { errors } = formState;

  useEffect(() => {
    const pathsWithErrors = hiddenQuestionPaths.filter(
      (path) => !!get(errors, path),
    );

    if (pathsWithErrors.length) {
      clearErrors(pathsWithErrors);
    }
  }, [hiddenQuestionPaths, clearErrors, get, errors]);
};
